import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import RateTable from './pages/RateTable';
import UploadData from './pages/UploadData';
import UploadRefusal from './pages/UploadRefusal';
import Results from './pages/Results';
import Calculate from './pages/Calculate';
import AddEditProvider from './pages/AddEditProvider';
import ProviderRateDetails from './pages/ProviderRateDetails';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Projections from './pages/Projections';
import ServiceTypes from './pages/ServiceTypes';
import NonPaymentOrders from './pages/NonPaymentOrders';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRole from './components/RequireRole';
import BatchList from './pages/payments/BatchList';
import BatchDetail from './pages/payments/BatchDetail';
import FinanceDashboard from './pages/payments/FinanceDashboard';
import UploadBatch from './pages/payments/UploadBatch';
import { AuthProvider } from './context/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              <Layout>
                <Login />
              </Layout>
            } />
            <Route path="/forgot-password" element={
              <Layout>
                <ForgotPassword />
              </Layout>
            } />
            <Route path="/reset-password" element={
              <Layout>
                <ResetPassword />
              </Layout>
            } />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={
                <Layout>
                  <Dashboard />
                </Layout>
              } />
              <Route path="/rate-table" element={
                <Layout>
                  <RateTable />
                </Layout>
              } />
              <Route path="/upload-data" element={
                <Layout>
                  <UploadData />
                </Layout>
              } />
              <Route path="/upload-refusal" element={
                <Layout>
                  <UploadRefusal />
                </Layout>
              } />
              <Route path="/provider/add" element={
                <Layout>
                  <ErrorBoundary>
                    <AddEditProvider />
                  </ErrorBoundary>
                </Layout>
              } />
              <Route path="/provider/edit/:id" element={
                <Layout>
                  <ErrorBoundary>
                    <AddEditProvider />
                  </ErrorBoundary>
                </Layout>
              } />
              <Route path="/provider/rates/:id" element={
                <Layout>
                  <ErrorBoundary>
                    <ProviderRateDetails />
                  </ErrorBoundary>
                </Layout>
              } />
              <Route path="/calculate/:uploadId" element={
                <Layout>
                  <Calculate />
                </Layout>
              } />
              <Route path="/results" element={
                <Layout>
                  <Results />
                </Layout>
              } />
              <Route path="/results/:resultId" element={
                <Layout>
                  <Results />
                </Layout>
              } />
              <Route path="/projections" element={
                <Layout>
                  <Projections />
                </Layout>
              } />
              <Route path="/projections/:projectionId" element={
                <Layout>
                  <Projections />
                </Layout>
              } />
              <Route path="/service-types" element={
                <Layout>
                  <ServiceTypes />
                </Layout>
              } />
              <Route path="/non-payment-orders" element={
                <Layout>
                  <NonPaymentOrders />
                </Layout>
              } />

              {/* Vendor Payments (Phase 4) — Operations & Finance */}
              <Route element={<RequireRole allowed={['operations', 'finance']} />}>
                <Route path="/payments/batches" element={
                  <Layout>
                    <ErrorBoundary>
                      <BatchList />
                    </ErrorBoundary>
                  </Layout>
                } />
                <Route path="/payments/batches/:id" element={
                  <Layout>
                    <ErrorBoundary>
                      <BatchDetail />
                    </ErrorBoundary>
                  </Layout>
                } />
              </Route>

              {/* Batch import — Operations only (server: operations/super_admin) */}
              <Route element={<RequireRole allowed={['operations']} />}>
                <Route path="/payments/upload" element={
                  <Layout>
                    <ErrorBoundary>
                      <UploadBatch />
                    </ErrorBoundary>
                  </Layout>
                } />
              </Route>

              {/* Finance Review (Phase 5) — Finance only */}
              <Route element={<RequireRole allowed={['finance']} />}>
                <Route path="/payments/finance" element={
                  <Layout>
                    <ErrorBoundary>
                      <FinanceDashboard />
                    </ErrorBoundary>
                  </Layout>
                } />
              </Route>
            </Route>

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
